const FEED_URL = "/api/activity-feed";
const PREVIEW_MANIFEST_URL = "/activity-previews.json";

const RESERVED_FOLDER_PREFIXES = ["待審核", "草稿", "_", "."];

function isValidCalendarDate(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function dateInfoFromFolderName(name) {
  const fullDate = name.match(/^(20\d{2})[._-]?(\d{2})[._-]?(\d{2})/);
  if (fullDate) {
    const [, yearText, monthText, dayText] = fullDate;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    if (isValidCalendarDate(year, month, day)) {
      const iso = `${yearText}-${monthText}-${dayText}`;
      return {
        date: iso,
        dateLabel: `${yearText}.${monthText}.${dayText}`,
        dateLong: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          timeZone: "UTC",
        }).format(new Date(`${iso}T00:00:00Z`)),
      };
    }
  }

  return {
    date: "",
    dateLabel: "日期待確認",
    dateLong: "日期待確認",
  };
}

function titleFromFolderName(name) {
  return name.trim();
}

function cohortFromFolderName(name) {
  if (name.includes("南北") || name.includes("全班")) return "all";
  if (name.includes("南班")) return "south";
  if (name.includes("北班")) return "north";
  return "all";
}

function categoryFromFolderName(name) {
  if (/參訪|企業|工廠/.test(name)) return "visit";
  if (/教師節|節慶|聖誕|新年|尾牙/.test(name)) return "festival";
  if (/出遊|旅行|旅遊|踏青/.test(name)) return "trip";
  return "gathering";
}

function isDisplayableFolder(folder) {
  return (
    folder &&
    typeof folder.id === "string" &&
    typeof folder.name === "string" &&
    folder.id.length > 0 &&
    folder.name.trim().length > 0 &&
    !RESERVED_FOLDER_PREFIXES.some((prefix) =>
      folder.name.trim().startsWith(prefix),
    )
  );
}

function safePreviewPhotos(value) {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (url) =>
      typeof url === "string" &&
      /^\/images\/activity-previews\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\.jpg$/.test(
        url,
      ),
  );
}

async function fetchPreviewManifest() {
  try {
    const response = await fetch(PREVIEW_MANIFEST_URL, {
      cache: "no-store",
      signal: AbortSignal.timeout(10_000),
    });
    if (!response.ok) return {};
    const payload = await response.json();
    return payload?.folders && typeof payload.folders === "object"
      ? payload
      : {};
  } catch {
    return {};
  }
}

export function toAutomaticActivity(folder) {
  const name = folder.name.trim();
  const dateInfo = dateInfoFromFolderName(name);
  return {
    id: `drive-folder-${folder.id}`,
    driveFolderId: folder.id,
    title: titleFromFolderName(name),
    titleEn: "",
    cohortBand: cohortFromFolderName(name),
    category: categoryFromFolderName(name),
    summary:
      "此活動已從 E118「活動歷程照片」資料夾自動同步。\n\n照片存放於 Google 雲端硬碟，登入具權限的帳號即可檢視。",
    highlights: [],
    tint: "sand",
    createdAt: typeof folder.createdAt === "string" ? folder.createdAt : "",
    updatedAt: typeof folder.updatedAt === "string" ? folder.updatedAt : "",
    previewPhotos: safePreviewPhotos(folder.previewPhotos),
    automatic: true,
    ...dateInfo,
  };
}

export async function fetchAutomaticActivities(onPreviewActivities = () => {}) {
  // Preview metadata is published with the images, so existing albums render
  // even when the live Drive endpoint is temporarily slow or unavailable.
  const responsePromise = fetch(FEED_URL, {
      cache: "no-store",
      signal: AbortSignal.timeout(30_000),
    }).catch(() => null);
  const previewManifest = await fetchPreviewManifest();
  const previewsByFolder = previewManifest.folders || {};
  const previewActivities = Object.entries(previewManifest.folderNames || {})
    .map(([id, name]) => ({ id, name, previewPhotos: previewsByFolder[id] || [] }))
    .filter(isDisplayableFolder)
    .map(toAutomaticActivity);
  if (previewActivities.length) onPreviewActivities(previewActivities);

  const response = await responsePromise;
  if (!response?.ok) return previewActivities;
  let payload;
  try {
    payload = await response.json();
  } catch {
    return previewActivities;
  }
  if (!Array.isArray(payload?.folders)) return previewActivities;
  return payload.folders.filter(isDisplayableFolder).map((folder) =>
    toAutomaticActivity({
      ...folder,
      previewPhotos: previewsByFolder[folder.id] || [],
    }),
  );
}

export function mergeActivities(curated, automatic) {
  const automaticByFolderId = new Map(
    automatic.map((activity) => [activity.driveFolderId, activity]),
  );
  const enrichedCurated = curated.map((activity) => {
    const automaticMatch = automaticByFolderId.get(activity.driveFolderId);
    if (
      !automaticMatch?.previewPhotos?.length ||
      activity.previewPhotos?.length
    )
      return activity;
    return { ...activity, previewPhotos: automaticMatch.previewPhotos };
  });
  const curatedFolderIds = new Set(
    enrichedCurated.map((activity) => activity.driveFolderId).filter(Boolean),
  );
  const uniqueAutomatic = automatic.filter(
    (activity) => !curatedFolderIds.has(activity.driveFolderId),
  );

  return [...enrichedCurated, ...uniqueAutomatic].sort((a, b) => {
    // Upload/creation timestamps are not event dates. Undated albums follow
    // the dated history, with a stable title order until a date is provided.
    const dateOrder = (b.date || "").localeCompare(a.date || "");
    return dateOrder || a.title.localeCompare(b.title, "zh-Hant") || a.id.localeCompare(b.id);
  });
}
